import React, { useEffect, useState, useRef } from "react";
import { Whisper, Popover, Tooltip } from "rsuite";
import styled from "styled-components";
import { DateTime } from "luxon";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import RemindFillIcon from "@rsuite/icons/RemindFill";
import { colors, shadows } from "../../../../../../ui";

export const RecentValuesFC = ({ recent_values, alertConfigs, tank }) => {
  const [heliumValue, setHeliumValue] = useState(recent_values?.helium_value);
  const [heliumUnits, setHeliumUnits] = useState(recent_values?.helium_units);
  const triggerRef = useRef();
  const [heliumAlert, setHeliumAlert] = useState();
  const [pressureAlert, setPressureAlert] = useState();
  const [heliumSpeaker, setHeliumSpeaker] = useState();
  const [pressureSpeaker, setPressureSpeaker] = useState();
  const [heliumModified, setHeliumModified] = useState(false);

  const modifierSpeaker = (
    <Tooltip>
      <>
        Helium Converted to Percent: <br /> Actual Reading:{" "}
        {recent_values?.helium_value} {recent_values?.helium_units}.
      </>
    </Tooltip>
  );

  const oldHeliumSpeaker = (
    <Tooltip>
      <>
        Helium Level Last Read: <br />{" "}
        {DateTime.fromISO(recent_values?.helium_datetime).toLocaleString(
          DateTime.DATETIME_MED,
        )}
        .
      </>
    </Tooltip>
  );

  const oldPressureSpeaker = (
    <Tooltip>
      <>
        Pressure Level Last Read: <br />{" "}
        {DateTime.fromISO(recent_values?.pressure_datetime).toLocaleString(
          DateTime.DATETIME_MED,
        )}
        .
      </>
    </Tooltip>
  );

  const setPressureAlerts = () => {
    if (alertConfigs) {
      let highestSeverity = "low";
      let pressureConfig = null;

      for (const config of alertConfigs) {
        if (
          config.field_name_alias.includes("He Pressure") ||
          config.field_name_alias.includes("Mag PSIA") ||
          config.field_name.includes("pressure") ||
          config.field_name.includes("he_psi")
        ) {
          if (
            (parseFloat(recent_values?.pressure_value) < config.threshold &&
              config.operator === "less_than") ||
            (parseFloat(recent_values?.pressure_value) > config.threshold &&
              config.operator === "greater_than")
          ) {
            if (highestSeverity !== "high") {
              highestSeverity = config.severity; // Update the highest severity
              pressureConfig = config; // Update highest config
            }
          }
        }
      }

      if (pressureConfig) {
        setPressureAlert(highestSeverity);
        setPressureSpeaker(
          <Popover
            title={
              <AlertText severity={pressureConfig.severity}>
                {pressureConfig.severity?.charAt(0).toUpperCase() +
                  pressureConfig.severity?.slice(1) +
                  " Pressure Alert"}
              </AlertText>
            }
          >
            {`${recent_values?.pressure_value} ${pressureConfig.threshold_units} ${pressureConfig.operator.replace(/_/g, " ")} ${pressureConfig.threshold} ${pressureConfig.threshold_units}`}
          </Popover>,
        );
      }
    }
  };

  const setHeliumAlerts = () => {
    let modifiedHelium = recent_values?.helium_value;

    if (tank && recent_values?.helium_units?.toUpperCase() === "LTRS") {
      setHeliumUnits("%");
      setHeliumValue(
        parseFloat((recent_values.helium_value / tank) * 100).toFixed(2),
      );
      setHeliumModified(true);
      modifiedHelium = parseFloat(
        (recent_values.helium_value / tank) * 100,
      ).toFixed(2);
    }

    if (alertConfigs) {
      let highestSeverity = "low";
      let heliumConfig = null;

      for (const config of alertConfigs) {
        if (
          (config.field_name.includes("he_level") ||
            config.field_name.includes("helium_level")) &&
          config.operator === "less_than"
        ) {
          if (modifiedHelium < config.threshold) {
            if (highestSeverity !== "high") {
              highestSeverity = config.severity; // Update the highest severity
              heliumConfig = config; // Update highest config
            }
          }
        }
      }

      if (heliumConfig) {
        setHeliumAlert(highestSeverity);
        setHeliumSpeaker(
          <Popover
            title={
              <AlertText
                severity={heliumConfig.severity}
              >{`${heliumConfig.severity.charAt(0).toUpperCase()}${heliumConfig.severity.slice(1)} Helium Alert`}</AlertText>
            }
          >
            {`${modifiedHelium} ${heliumConfig.threshold_units} ${heliumConfig.operator.replace(/_/g, " ")} ${heliumConfig.threshold} ${heliumConfig.threshold_units}`}
          </Popover>,
        );
      }
    }
  };

  useEffect(() => {
    setHeliumAlerts();
    setPressureAlerts();
  }, [recent_values, alertConfigs]);

  return recent_values ? (
    <>
      {recent_values?.pressure_value ? (
        <DataBox>
          <IconSlot>
            {Date.now() - new Date(recent_values.pressure_datetime).getTime() >
            24 * 60 * 60 * 1000 ? (
              <Whisper
                placement="right"
                speaker={oldPressureSpeaker}
                ref={triggerRef}
                trigger="hover"
                key={"old_pressure"}
              >
                <RemindFillIcon color="orange" />
              </Whisper>
            ) : null}
          </IconSlot>
          <LabelText>Pressure: </LabelText>
          {pressureAlert ? (
            <Whisper
              placement="right"
              speaker={pressureSpeaker}
              ref={triggerRef}
              trigger="hover"
              key={"helium"}
            >
              <SmallText severity={pressureAlert}>
                {recent_values.pressure_units?.toLowerCase() === "mbar"
                  ? parseFloat(recent_values?.pressure_value).toFixed(2)
                  : parseFloat(recent_values?.pressure_value).toFixed(3)}
              </SmallText>
            </Whisper>
          ) : (
            <SmallText severity={pressureAlert}>
              {recent_values.pressure_units?.toLowerCase() === "mbar"
                ? parseFloat(recent_values?.pressure_value).toFixed(2)
                : parseFloat(recent_values?.pressure_value).toFixed(3)}
            </SmallText>
          )}
          <UnitText>{recent_values.pressure_units}</UnitText>
        </DataBox>
      ) : null}
      {heliumValue ? (
        <DataBox>
          <IconSlot>
            {Date.now() - new Date(recent_values.helium_datetime).getTime() >
            24 * 60 * 60 * 1000 ? (
              <Whisper
                placement="right"
                speaker={oldHeliumSpeaker}
                ref={triggerRef}
                trigger="hover"
                key={"old_helium"}
              >
                <RemindFillIcon color="orange" />
              </Whisper>
            ) : null}
          </IconSlot>
          <LabelText>
            {heliumModified ? (
              <Whisper
                placement="top"
                controlId="control-id-click"
                trigger="hover"
                speaker={modifierSpeaker}
              >
                <InfoOutlineIcon color="green" />
              </Whisper>
            ) : null}{" "}
            HE Level:
          </LabelText>
          {heliumAlert ? (
            <Whisper
              placement="right"
              speaker={heliumSpeaker}
              ref={triggerRef}
              trigger="hover"
              key={"helium"}
            >
              <SmallText severity={heliumAlert}>
                {parseFloat(heliumValue).toFixed(2)}
              </SmallText>
            </Whisper>
          ) : (
            <SmallText severity={heliumAlert}>
              {parseFloat(heliumValue).toFixed(2)}
            </SmallText>
          )}
          <UnitText>{heliumUnits}</UnitText>
        </DataBox>
      ) : null}
    </>
  ) : null;
};

const DataBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  margin: 2px 0;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${colors.neutral[700]};
`;

const IconSlot = styled.div`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LabelText = styled.div`
  min-width: 65px;
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 500;
  color: ${colors.neutral[600]};

  @media screen and (max-width: 769px) {
    min-width: 70px;
    font-size: 11px;
  }
`;

const SmallText = styled.div`
  display: inline-block;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;

  color: ${({ severity }) =>
    severity === "high"
      ? colors.error.main
      : severity === "medium"
        ? colors.warning.main
        : severity === "low"
          ? colors.neutral[600]
          : colors.primary.main};

  background-color: ${({ severity }) =>
    severity === "high"
      ? colors.error.light
      : severity === "medium"
        ? colors.warning.light
        : severity === "low"
          ? colors.neutral[200]
          : "rgba(44, 90, 160, 0.1)"};

  border: 1px solid
    ${({ severity }) =>
      severity === "high"
        ? colors.error.border
        : severity === "medium"
          ? colors.warning.border
          : severity === "low"
            ? colors.neutral[300]
            : "rgba(44, 90, 160, 0.2)"};

  @media screen and (max-width: 769px) {
    font-size: 12px;
    padding: 3px 6px;
  }
`;

const UnitText = styled.div`
  display: inline-block;
  font-size: 10px;
  font-weight: 400;
  color: ${colors.neutral[600]};
  margin-left: 3px;

  @media screen and (max-width: 769px) {
    font-size: 10px;
  }
`;

const AlertText = styled.div`
  color: ${({ severity }) =>
    severity === "high"
      ? colors.error.main
      : severity === "medium"
        ? colors.warning.main
        : severity === "low"
          ? colors.neutral[600]
          : colors.primary.main};
  background-color: ${({ severity }) =>
    severity === "high"
      ? colors.error.light
      : severity === "medium"
        ? colors.warning.light
        : severity === "low"
          ? colors.neutral[200]
          : "rgba(44, 90, 160, 0.1)"};
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  border-radius: 8px;
  border: 1px solid
    ${({ severity }) =>
      severity === "high"
        ? colors.error.border
        : severity === "medium"
          ? colors.warning.border
          : severity === "low"
            ? colors.neutral[300]
            : "rgba(44, 90, 160, 0.2)"};
`;
